import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  StickyNote2 as NoteIcon,
  Campaign as ActivityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Circle as CircleIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import LeadService from "../services/LeadService";
import QuotationService from "../services/QuotationService";
import ConversationService from "../services/ConversationService";
import LeadRemindersEditor from "./LeadRemindersEditor";
import QuotationDetailDialog from "./QuotationDetailDialog";
import QuotationSendDialog from "./QuotationSendDialog";
import MyAccountService from "../services/MyAccountService";
import appConfig from "../shared/js/app-config";
import {
  LEAD_PRIORITIES,
  priorityColor,
  getCategoryColor,
  getInitials,
  timeAgo,
  formatDateTime,
  formatDateTimeWithAgo,
  formatTime,
} from "./leadConstants";
import { formatMoney, formatQuotationLabel } from "../utils/quotationUtils";

const PURPLE = "#5c4fd6";
const PURPLE_LIGHT = "#ebe8f8";
const PURPLE_DARK = "#3d3580";
const PAGE_BG = "#eef0f7";

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        mb: 1,
        mt: 2,
      }}
    >
      {children}
    </Typography>
  );
}

function DetailCard({ title, action, onAction, children, badge }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mx: 1.5,
        mb: 1.5,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #e4e6ef",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          bgcolor: PURPLE_LIGHT,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color={PURPLE_DARK}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {badge}
          {action && (
            <Button
              type="button"
              size="small"
              onClick={onAction}
              sx={{
                minWidth: 0,
                fontWeight: 700,
                color: PURPLE,
                fontSize: "0.7rem",
                letterSpacing: "0.04em",
              }}
            >
              {action}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff" }}>{children}</Box>
    </Paper>
  );
}

function ContactRow({ icon, value, actions }) {
  if (!value) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.75,
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {icon}
        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
          {value}
        </Typography>
      </Box>
      {actions}
    </Box>
  );
}

function JourneyItem({ label, at, isLast }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <CircleIcon sx={{ fontSize: 14, color: PURPLE }} />
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, minHeight: 24, bgcolor: "#d8d4f0", my: 0.5 }} />
        )}
      </Box>
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          mb: isLast ? 0 : 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          borderColor: "#e8e6f2",
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
          {formatDateTime(at)}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {timeAgo(at)}
        </Typography>
      </Paper>
    </Box>
  );
}

const staffDisplayName = (row) =>
  `${row?.first_name || ""} ${row?.last_name || ""}`.trim() || row?.email || "Team member";

const LeadDetailPanel = ({
  lead,
  stages,
  categories = [],
  onClose,
  onRefresh,
  onMoveStage,
  onDelete,
  onMessage,
}) => {
  const navigate = useNavigate();
  const [ownerName, setOwnerName] = useState("—");
  const [staff, setStaff] = useState([]);
  const [editingMeta, setEditingMeta] = useState(false);
  const [title, setTitle] = useState(lead.title || "");
  const [description, setDescription] = useState(lead.description || "");
  const [priority, setPriority] = useState(lead.priority || "normal");
  const [stageId, setStageId] = useState(lead.stage_id || "");
  const [categoryId, setCategoryId] = useState(lead.category_id || "");
  const [assigneeAccountId, setAssigneeAccountId] = useState(lead.assignee_account_id || "");
  const [savingMeta, setSavingMeta] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", due_at: "" });
  const [commentText, setCommentText] = useState("");
  const [editComment, setEditComment] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotationDetailOpen, setQuotationDetailOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingQuotation, setSendingQuotation] = useState(false);
  const [convertingQuotation, setConvertingQuotation] = useState(false);
  const [invoicingQuotation, setInvoicingQuotation] = useState(false);

  useEffect(() => {
    setTitle(lead.title || "");
    setDescription(lead.description || "");
    setPriority(lead.priority || "normal");
    setStageId(lead.stage_id || "");
    setCategoryId(lead.category_id || "");
    setAssigneeAccountId(lead.assignee_account_id || "");
    setEditingMeta(false);
  }, [lead.id]);

  useEffect(() => {
    if (!editingMeta) {
      setTitle(lead.title || "");
      setDescription(lead.description || "");
      setPriority(lead.priority || "normal");
      setStageId(lead.stage_id || "");
      setCategoryId(lead.category_id || "");
      setAssigneeAccountId(lead.assignee_account_id || "");
    }
  }, [lead.title, lead.description, lead.priority, lead.stage_id, lead.category_id, lead.assignee_account_id, editingMeta]);

  useEffect(() => {
    MyAccountService.fetch()
      .then((account) => setOwnerName(account?.getFullName?.() || "—"))
      .catch(() => setOwnerName("—"));
    ConversationService.getRoutingStaff()
      .then((rows) => setStaff(Array.isArray(rows) ? rows : []))
      .catch(() => setStaff([]));
  }, []);

  const loadQuotations = useCallback(async () => {
    if (!lead?.id) return;
    setQuotationsLoading(true);
    try {
      const data = await QuotationService.getQuotations({ leadId: lead.id, limit: 20 });
      setQuotations(data.quotations || []);
    } catch {
      setQuotations([]);
    } finally {
      setQuotationsLoading(false);
    }
  }, [lead?.id]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  const storeName = appConfig.getStoreName() || "Workspace";
  const contactName = lead.contact_name || lead.title;
  const phone = lead.contact_phone || "";
  const phoneDigits = phone.replace(/\D/g, "");
  const comments = lead.comments || [];
  const tasks = lead.tasks || [];
  const openTasks = tasks.filter((t) => t.status !== "done").length;
  const contactHref = (() => {
    const params = new URLSearchParams();
    if (lead.contact_id) params.set("id", lead.contact_id);
    else if (phone) params.set("q", phone);
    const qs = params.toString();
    return qs ? `/contacts?${qs}` : "/contacts";
  })();
  const contactEditHref = `${contactHref}${contactHref.includes("?") ? "&" : "?"}edit=1`;
  const canOpenContact = Boolean(lead.contact_id || phone);

  const journeyEvents = [
    { label: "Lead Created", at: lead.created_at },
    ...comments.map((c) => ({
      label: `Note by ${c.account_name}`,
      at: c.created_at,
    })),
  ]
    .sort((a, b) => new Date(b.at) - new Date(a.at))
    .slice(0, 6);

  const handleSaveMeta = async () => {
    if (!title.trim()) {
      onMessage?.("Lead title is required", "error");
      return;
    }
    setSavingMeta(true);
    try {
      if (stageId && stageId !== lead.stage_id) {
        await LeadService.updateLeadStage(lead.id, stageId);
      }
      await LeadService.updateLead(lead.id, {
        title: title.trim(),
        description: description.trim() || null,
        priority,
        category_id: categoryId || null,
        assignee_account_id: assigneeAccountId || null,
      });
      setEditingMeta(false);
      await onRefresh?.(lead.id);
      onMessage?.("Lead updated", "success");
    } catch {
      onMessage?.("Failed to update lead", "error");
    } finally {
      setSavingMeta(false);
    }
  };

  const handleCancelMeta = () => {
    setTitle(lead.title || "");
    setDescription(lead.description || "");
    setPriority(lead.priority || "normal");
    setStageId(lead.stage_id || "");
    setCategoryId(lead.category_id || "");
    setAssigneeAccountId(lead.assignee_account_id || "");
    setEditingMeta(false);
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    await LeadService.createTask(lead.id, {
      title: newTask.title.trim(),
      due_at: newTask.due_at || null,
    });
    setTaskDialogOpen(false);
    setNewTask({ title: "", due_at: "" });
    onRefresh();
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    await LeadService.updateTask(lead.id, task.id, { status: newStatus });
    onRefresh();
  };

  const handleDeleteTask = async (taskId) => {
    await LeadService.deleteTask(lead.id, taskId);
    onRefresh();
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    if (editComment) {
      await LeadService.updateComment(lead.id, editComment.id, commentText.trim());
      setEditComment(null);
    } else {
      await LeadService.createComment(lead.id, commentText.trim());
    }
    setCommentText("");
    setNoteDialogOpen(false);
    onRefresh();
  };

  const handleDeleteComment = async (commentId) => {
    await LeadService.deleteComment(lead.id, commentId);
    onRefresh();
  };

  const openQuotationDetail = async (quotation) => {
    try {
      const full = await QuotationService.getQuotationById(quotation.id);
      setSelectedQuotation(full);
      setQuotationDetailOpen(true);
    } catch {
      onMessage?.("Failed to load quotation", "error");
    }
  };

  const handleDownloadQuotationPdf = async (quotation) => {
    setDownloadingPdf(true);
    try {
      const { blob, filename } = await QuotationService.downloadPdf(quotation.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onMessage?.("PDF downloaded", "success");
    } catch {
      onMessage?.("Failed to download PDF", "error");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendQuotation = async (payload) => {
    if (!sendTarget) return;
    setSendingQuotation(true);
    try {
      const result = await QuotationService.sendQuotation(sendTarget.id, payload);
      setSelectedQuotation(result?.quotation || sendTarget);
      onMessage?.("Quotation sent via WhatsApp", "success");
      setSendDialogOpen(false);
      setSendTarget(null);
      loadQuotations();
    } catch (err) {
      onMessage?.(
        err?.response?.data?.message || err?.message || "Failed to send quotation",
        "error",
      );
      throw err;
    } finally {
      setSendingQuotation(false);
    }
  };

  const handleConvertQuotationToOrder = async (quotation, deliveryAddress, paymentMode = "cod") => {
    setConvertingQuotation(true);
    try {
      const result = await QuotationService.convertToOrder(quotation.id, {
        deliveryAddress,
        paymentMode,
      });
      setSelectedQuotation(result?.quotation || quotation);
      loadQuotations();
      let message;
      if (result?.delivery?.mode === "native_order_details") {
        message = "Quotation converted. WhatsApp payment request sent to customer.";
      } else if (result?.delivery?.mode === "cod_confirmation") {
        message = "Quotation converted. COD order created and confirmation sent on WhatsApp.";
      } else {
        message = "Quotation converted to order.";
      }
      onMessage?.(message, "success");
    } catch (err) {
      onMessage?.(
        err?.response?.data?.message || err?.message || "Failed to convert quotation",
        "error",
      );
      throw err;
    } finally {
      setConvertingQuotation(false);
    }
  };

  const handleConvertQuotationToInvoice = async (quotation) => {
    if (!window.confirm(`Send proforma invoice for ${formatQuotationLabel(quotation.quotation_number)}?`)) return;
    setInvoicingQuotation(true);
    try {
      const result = await QuotationService.convertToInvoice(quotation.id);
      setSelectedQuotation(result?.quotation || quotation);
      loadQuotations();
      onMessage?.("Proforma invoice sent to customer", "success");
    } catch (err) {
      onMessage?.(
        err?.response?.data?.message || err?.message || "Failed to send invoice",
        "error",
      );
    } finally {
      setInvoicingQuotation(false);
    }
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 400 },
        flexShrink: 0,
        maxHeight: "calc(100vh - 120px)",
        overflow: "auto",
        bgcolor: PAGE_BG,
        borderLeft: "1px solid #dfe2ec",
      }}
    >
      {/* Top strip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          bgcolor: PURPLE_LIGHT,
          borderBottom: "1px solid #d8d4f0",
        }}
      >
        <Typography variant="body2" fontWeight={700} color={PURPLE_DARK} sx={{ textTransform: "capitalize" }}>
          {lead.stage_name || "New"} lead
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" fontWeight={700} color={PURPLE_DARK}>
            Assigned: {lead.assignee_account_name || ownerName || "—"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: PURPLE_DARK }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Profile card */}
      <Paper
        elevation={0}
        sx={{
          mx: 1.5,
          mt: 1.5,
          mb: 1.5,
          borderRadius: 2,
          border: "1px solid #e4e6ef",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: PURPLE,
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {getInitials(contactName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} color={PURPLE_DARK} noWrap>
                {contactName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {lead.title}
              </Typography>
              {(lead.created_at || lead.updated_at) && (
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                  {lead.created_at && `Created ${formatDateTime(lead.created_at)}`}
                  {lead.created_at && lead.updated_at && " · "}
                  {lead.updated_at && `Updated ${formatDateTime(lead.updated_at)}`}
                </Typography>
              )}
            </Box>
            {phoneDigits && (
              <IconButton
                size="small"
                href={`tel:${phone}`}
                sx={{
                  bgcolor: PURPLE,
                  color: "#fff",
                  borderRadius: 1,
                  "&:hover": { bgcolor: PURPLE_DARK },
                }}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
            {lead.contact_company && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <BusinessIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {lead.contact_company}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <BusinessIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                {storeName}
              </Typography>
            </Box>
            {lead.source && (
              <Typography variant="caption" color="text.secondary">
                Ref: {lead.source}
              </Typography>
            )}
          </Box>

          <SectionLabel>Recent Interactions</SectionLabel>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {comments.length} {comments.length === 1 ? "note" : "notes"}
              </Typography>
              <Avatar sx={{ width: 36, height: 36, bgcolor: PURPLE, mx: "auto" }}>
                <NoteIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ fontWeight: 600 }}>
                {comments.length > 0
                  ? formatTime(comments[comments.length - 1].created_at)
                  : "—"}
              </Typography>
              {comments.length > 0 && (
                <Typography variant="caption" color="text.disabled" display="block">
                  {timeAgo(comments[comments.length - 1].created_at)}
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {openTasks} open tasks
              </Typography>
              <Avatar sx={{ width: 36, height: 36, bgcolor: PURPLE, mx: "auto" }}>
                <ActivityIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ fontWeight: 600 }}>
                {tasks[0] ? formatTime(tasks[0].created_at) : "—"}
              </Typography>
              {tasks[0] && (
                <Typography variant="caption" color="text.disabled" display="block">
                  {timeAgo(tasks[0].created_at)}
                </Typography>
              )}
            </Box>
          </Box>

          <SectionLabel>Contact</SectionLabel>
          <ContactRow
            icon={<PhoneIcon sx={{ fontSize: 16, color: PURPLE }} />}
            value={phone || "—"}
            actions={
              phoneDigits ? (
                <Box>
                  <IconButton size="small" href={`tel:${phone}`} sx={{ color: PURPLE }}>
                    <PhoneIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={`https://wa.me/${phoneDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "#25D366" }}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : null
            }
          />
          <ContactRow
            icon={<EmailIcon sx={{ fontSize: 16, color: PURPLE }} />}
            value={lead.contact_email}
          />
          <ContactRow
            icon={<LocationIcon sx={{ fontSize: 16, color: PURPLE }} />}
            value={lead.contact_address}
          />
          {canOpenContact && (
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => navigate(contactHref)}
                sx={{ textTransform: "none" }}
              >
                Open contact
              </Button>
              <Button
                size="small"
                variant="text"
                startIcon={<EditIcon />}
                onClick={() => navigate(contactEditHref)}
                sx={{ textTransform: "none" }}
              >
                Edit contact
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* More on this lead */}
      <DetailCard
        title="More On This Lead"
        action={editingMeta ? undefined : "MODIFY"}
        onAction={() => setEditingMeta(true)}
      >
        {editingMeta ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              label="Lead title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <FormControl fullWidth size="small">
              <InputLabel>Stage</InputLabel>
              <Select
                value={stageId}
                label="Stage"
                onChange={(e) => setStageId(e.target.value)}
              >
                {stages.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryId}
                label="Category"
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Assign to</InputLabel>
              <Select
                value={assigneeAccountId}
                label="Assign to"
                onChange={(e) => setAssigneeAccountId(e.target.value)}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {staff.map((row) => (
                  <MenuItem key={row.id} value={row.id}>
                    {staffDisplayName(row)}
                    {row.role === "owner" ? " (owner)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value)}
              >
                {LEAD_PRIORITIES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button type="button" size="small" onClick={handleCancelMeta}>Cancel</Button>
              <Button
                type="button"
                size="small"
                variant="contained"
                onClick={handleSaveMeta}
                disabled={savingMeta}
                sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
              >
                {savingMeta ? "Saving…" : "Save"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {lead.title}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <Chip label={lead.stage_name} size="small" sx={{ bgcolor: PURPLE_LIGHT, color: PURPLE_DARK }} />
              {lead.category_name && (
                <Chip
                  label={lead.category_name}
                  size="small"
                  sx={{
                    bgcolor: `${getCategoryColor(lead)}22`,
                    color: getCategoryColor(lead),
                  }}
                />
              )}
              <Chip
                label={priority}
                size="small"
                color={priorityColor(priority)}
                sx={{ textTransform: "capitalize" }}
              />
              {(lead.assignee_account_name || lead.assignee_account_id) && (
                <Chip
                  label={lead.assignee_account_name || "Assigned"}
                  size="small"
                  sx={{ bgcolor: PURPLE_LIGHT, color: PURPLE_DARK }}
                />
              )}
            </Box>
            {description ? (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {description}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No description set
              </Typography>
            )}
            {(lead.created_at || lead.updated_at) && (
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1.5 }}>
                {lead.created_at && `Created ${formatDateTime(lead.created_at)}`}
                {lead.created_at && lead.updated_at && " · "}
                {lead.updated_at && `Updated ${formatDateTime(lead.updated_at)}`}
              </Typography>
            )}
          </Box>
        )}
      </DetailCard>

      {/* Reminders */}
      <DetailCard title="Reminders">
        <LeadRemindersEditor
          compact
          reminders={lead.reminders || []}
          leadId={lead.id}
          onRefresh={onRefresh}
        />
      </DetailCard>

      {/* Lead journey */}
      <DetailCard
        title="Lead Journey"
        badge={(
          <Chip
            label={lead.stage_name}
            size="small"
            sx={{ bgcolor: PURPLE, color: "#fff", fontWeight: 600, fontSize: "0.65rem" }}
          />
        )}
      >
        {journeyEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No journey events yet
          </Typography>
        ) : (
          journeyEvents.map((event, idx) => (
            <JourneyItem
              key={`${event.label}-${event.at}`}
              label={event.label}
              at={event.at}
              isLast={idx === journeyEvents.length - 1}
            />
          ))
        )}
      </DetailCard>

      {/* Quotations */}
      <DetailCard
        title="Quotations"
        action="CREATE"
        onAction={() => navigate(`/quotations/new?leadId=${lead.id}`)}
      >
        {quotationsLoading ? (
          <Typography variant="body2" color="text.secondary">Loading quotations...</Typography>
        ) : quotations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No quotations for this lead yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {quotations.map((quotation) => (
              <Paper
                key={quotation.id}
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 1.5, borderColor: "#eceaf5", cursor: "pointer" }}
                onClick={() => openQuotationDetail(quotation)}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {formatQuotationLabel(quotation.quotation_number)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatMoney(quotation.total_minor, quotation.currency)}
                      {" · "}
                      {quotation.status}
                    </Typography>
                  </Box>
                  <Chip size="small" label={quotation.status} sx={{ textTransform: "capitalize" }} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DetailCard>

      {/* Tasks */}
      <DetailCard
        title="Tasks"
        action="ADD"
        onAction={() => setTaskDialogOpen(true)}
      >
        {tasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tasks yet
          </Typography>
        ) : (
          <List dense disablePadding>
            {tasks.map((task) => (
              <ListItem
                key={task.id}
                disablePadding
                sx={{ py: 0.5 }}
                secondaryAction={(
                  <IconButton edge="end" size="small" onClick={() => handleDeleteTask(task.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <Checkbox
                  checked={task.status === "done"}
                  onChange={() => handleToggleTask(task)}
                  size="small"
                  sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                />
                <ListItemText
                  primary={task.title}
                  secondary={task.due_at ? `Due ${formatDateTime(task.due_at)}` : null}
                  sx={{ textDecoration: task.status === "done" ? "line-through" : "none" }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DetailCard>

      {/* Notes */}
      <DetailCard
        title="Notes"
        action="ADD"
        onAction={() => {
          setEditComment(null);
          setCommentText("");
          setNoteDialogOpen(true);
        }}
      >
        {comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No notes yet
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {comments.map((comment) => (
              <Paper key={comment.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, borderColor: "#eceaf5" }}>
                <Typography variant="caption" color="text.secondary">
                  {comment.account_name} · {formatDateTimeWithAgo(comment.created_at)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {comment.body}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    sx={{ color: PURPLE, minWidth: 0, px: 0.5 }}
                    onClick={() => {
                      setEditComment(comment);
                      setCommentText(comment.body);
                      setNoteDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    sx={{ minWidth: 0, px: 0.5 }}
                    onClick={() => handleDeleteComment(comment.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DetailCard>

      <Box sx={{ px: 2, py: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Button color="error" size="small" onClick={() => onDelete(lead.id)}>
          Delete lead
        </Button>
      </Box>

      <QuotationDetailDialog
        open={quotationDetailOpen}
        quotation={selectedQuotation}
        onClose={() => {
          setQuotationDetailOpen(false);
          setSelectedQuotation(null);
        }}
        onEdit={(quotation) => {
          setQuotationDetailOpen(false);
          navigate(`/quotations/${quotation.id}/edit`);
        }}
        onDelete={async (quotation) => {
          if (!window.confirm(`Delete ${formatQuotationLabel(quotation.quotation_number)}?`)) return;
          await QuotationService.deleteQuotation(quotation.id);
          setQuotationDetailOpen(false);
          setSelectedQuotation(null);
          loadQuotations();
          onMessage?.("Quotation deleted", "success");
        }}
        onStatusChange={async (quotation, status) => {
          const updated = await QuotationService.updateStatus(quotation.id, status);
          setSelectedQuotation(updated);
          loadQuotations();
          onMessage?.("Quotation status updated", "success");
        }}
        onDownloadPdf={handleDownloadQuotationPdf}
        onSendWhatsApp={(quotation) => {
          setSendTarget(quotation);
          setSendDialogOpen(true);
        }}
        onConvertToOrder={handleConvertQuotationToOrder}
        onConvertToInvoice={handleConvertQuotationToInvoice}
        downloading={downloadingPdf}
        sending={sendingQuotation}
        converting={convertingQuotation}
        invoicing={invoicingQuotation}
      />

      <QuotationSendDialog
        open={sendDialogOpen}
        quotation={sendTarget}
        onClose={() => {
          setSendDialogOpen(false);
          setSendTarget(null);
        }}
        onSend={handleSendQuotation}
      />

      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add task</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Title"
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            fullWidth
          />
          <TextField
            label="Due date"
            type="datetime-local"
            value={newTask.due_at}
            onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTask} sx={{ bgcolor: PURPLE }}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editComment ? "Edit note" : "Add note"}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Write a note..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddComment} sx={{ bgcolor: PURPLE }}>
            {editComment ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadDetailPanel;
